/**
 * useEffectiveManifest hook
 *
 * Shared logic for building a ConnectorManifestWithParameters from connector metadata.
 * Used by both EditConnectionDialog and ConnectionCreationPanel.
 */

import { useMemo } from 'react';
import type { ConnectionParameterSet } from '@/types/connectionParameters';
import type { ConnectorManifestWithParameters } from '@/services/manifestConnectionService';
import type { ConnectorMetadata } from '@/services/connectionApi';

/**
 * Pure function to build a ConnectorManifestWithParameters from connector metadata.
 * Handles both multi-auth (connectionParameterSets) and legacy single-auth (connectionParameters) formats.
 */
function buildManifestFromMetadata(
  metadata: ConnectorMetadata
): ConnectorManifestWithParameters {
  const manifest: ConnectorManifestWithParameters = {
    id: metadata.id,
    name: metadata.name,
  };

  // Include connectionParameters (legacy single-auth)
  if (metadata.connectionParameters) {
    manifest.connectionParameters = metadata.connectionParameters;
  }

  // Include connectionParameterSets (multi-auth)
  if (metadata.connectionParameterSets) {
    const transformedSets = metadata.connectionParameterSets.map(
      (set): ConnectionParameterSet => ({
        name: set.name,
        uiDefinition: {
          displayName: set.uiDefinition?.displayName ?? set.name,
          ...(set.uiDefinition?.description ? { description: set.uiDefinition.description } : {}),
        },
        parameters: (set.parameters ?? {}) as ConnectionParameterSet['parameters'],
      })
    );
    manifest.connectionParameterSets = { values: transformedSets };
  }

  // Include connectionAlternativeParameters if available
  if (metadata.connectionAlternativeParameters) {
    manifest.connectionAlternativeParameters = metadata.connectionAlternativeParameters;
  }

  return manifest;
}

/**
 * Builds a ConnectorManifestWithParameters from connector metadata.
 * Handles both multi-auth (connectionParameterSets) and legacy single-auth (connectionParameters) formats.
 *
 * @param connectorMetadata - The connector metadata from the API or manifest
 * @returns ConnectorManifestWithParameters or null if no metadata provided
 */
export function useEffectiveManifest(
  connectorMetadata: ConnectorMetadata | null | undefined
): ConnectorManifestWithParameters | null {
  return useMemo(() => {
    if (!connectorMetadata) return null;
    return buildManifestFromMetadata(connectorMetadata);
  }, [connectorMetadata]);
}

/**
 * Builds a ConnectorManifestWithParameters from a provided manifest OR fetched metadata.
 * Used by ConnectionCreationPanel which may receive a manifest prop or fetch metadata.
 *
 * @param connectorManifest - Optional manifest prop (takes precedence)
 * @param fetchedMetadata - Optional fetched connector metadata
 * @returns ConnectorManifestWithParameters or null if neither is provided
 */
export function useEffectiveManifestWithFallback(
  connectorManifest: ConnectorManifestWithParameters | null | undefined,
  fetchedMetadata: ConnectorMetadata | null | undefined
): ConnectorManifestWithParameters | null {
  return useMemo(() => {
    // Prefer prop if provided
    if (connectorManifest) {
      return connectorManifest;
    }

    // Use fetched metadata if available
    if (fetchedMetadata) {
      return buildManifestFromMetadata(fetchedMetadata);
    }

    return null;
  }, [connectorManifest, fetchedMetadata]);
}
